import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Pencil, Trash2, Eye } from 'lucide-react'
import { customersApi, Customer, CustomerFormData } from '../../lib/api/customers'

export const Route = createFileRoute('/dashboard/customers/')({
  component: CustomersComponent,
})

function CustomersComponent() {
  const [searchQuery, setSearchQuery] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const queryClient = useQueryClient()

  const { data: currentUserData } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await fetch('/api/v1/current_user', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      return response.json()
    }
  })

  const currentUser = currentUserData?.user
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner'

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', searchQuery],
    queryFn: () => customersApi.getAll(searchQuery ? { q: searchQuery } : undefined),
  })

  const createMutation = useMutation({
    mutationFn: customersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setIsModalOpen(false)
      setEditingCustomer(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CustomerFormData }) =>
      customersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setIsModalOpen(false)
      setEditingCustomer(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: customersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data: CustomerFormData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      notes: formData.get('notes') as string,
    }

    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer)
    setIsModalOpen(true)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this customer?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingCustomer(null)
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Customers</h1>
            <p className="text-base-content/70 mt-1">
              Manage your customer database
            </p>
          </div>
          {isAdmin && (
            <button
              className="btn btn-accent text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Add Customer
            </button>
          )}
        </div>

        <div className="form-control">
          <input
            type="text"
            placeholder="Search customers by name, email, or phone..."
            className="input input-bordered w-full max-w-md"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {isLoading && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center py-8">Loading customers...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <span>Error: {error.message}</span>
          </div>
        )}

        {data && data.customers && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.customers.map((customer) => (
                      <tr key={customer.id}>
                        <td className="font-semibold">{customer.name}</td>
                        <td>{customer.email || '-'}</td>
                        <td>{customer.phone || '-'}</td>
                        <td className="max-w-xs truncate">
                          {customer.notes || '-'}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <Link
                              to="/dashboard/customers/$id"
                              params={{ id: customer.id.toString() }}
                              className="btn btn-sm btn-ghost"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            {isAdmin && (
                              <>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => handleEdit(customer)}
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  className="btn btn-sm btn-ghost text-error"
                                  onClick={() => handleDelete(customer.id)}
                                  disabled={deleteMutation.isPending}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {data && data.customers && data.customers.length === 0 && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center text-base-content/50 py-8">
                No customers found. Click "Add Customer" to create your first customer!
              </p>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name *</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="input input-bordered"
                  required
                  defaultValue={editingCustomer?.name}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="input input-bordered"
                  defaultValue={editingCustomer?.email}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Phone</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="input input-bordered"
                  defaultValue={editingCustomer?.phone}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Notes</span>
                </label>
                <textarea
                  name="notes"
                  className="textarea textarea-bordered h-24"
                  defaultValue={editingCustomer?.notes}
                />
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-accent text-white"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingCustomer
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>
      )}
    </>
  )
}
